import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { useAuth } from '../../contexts/AuthContext';
import AddSalesListingModal from './AddSalesListingModal';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import JsBarcode from 'jsbarcode';
import * as FiIcons from 'react-icons/fi';

const { 
  FiSearch, FiFilter, FiGlobe, FiExternalLink, 
  FiCheckCircle, FiXCircle, FiClock, FiTruck, 
  FiShoppingBag, FiTag, FiRefreshCw, FiPrinter,
  FiEdit3, FiX, FiCheck
} = FiIcons;

const SalesListingReport = () => {
  const { products, updateSalesListing } = useInventory();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  
  // Modal States
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  const searchInputRef = useRef(null);

  // Flatten and prepare data
  const allListings = useMemo(() => {
    return products.flatMap(product => 
      (product.salesListings || []).map(listing => ({
        ...listing,
        productName: product.name,
        productSku: product.localSku,
        productId: product.id,
        productImage: product.image,
        productQty: product.quantity
      }))
    ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [products]);

  // Platforms for filter
  const platforms = [...new Set(allListings.map(l => l.platform))].sort();

  // Filter Logic
  const filteredListings = useMemo(() => {
    return allListings.filter(item => {
      const matchesSearch = 
        (item.sales_id && item.sales_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.productSku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.platform.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
      const matchesPlatform = filterPlatform === 'all' || item.platform === filterPlatform;

      return matchesSearch && matchesStatus && matchesPlatform;
    });
  }, [allListings, searchTerm, filterStatus, filterPlatform]);

  // Status Configuration for UI
  const statusConfig = {
    'active': { label: 'Active', color: 'bg-green-100 text-green-800', icon: FiGlobe },
    'pending_shipping': { label: 'Pending Ship', color: 'bg-orange-100 text-orange-800', icon: FiClock },
    'waiting_for_pickup': { label: 'Wait Pickup', color: 'bg-purple-100 text-purple-800', icon: FiTruck },
    'sold': { label: 'Sold', color: 'bg-gray-800 text-white', icon: FiCheckCircle },
    'canceled': { label: 'Canceled', color: 'bg-gray-200 text-gray-600', icon: FiXCircle },
    'paused': { label: 'Paused', color: 'bg-yellow-100 text-yellow-800', icon: FiClock }
  };

  const handleStatusChange = async (listing, newStatus) => {
    if (listing.status === newStatus) return;
    
    toast.promise(
      updateSalesListing(listing.productId, listing.id, { status: newStatus }),
      {
        loading: 'Updating status...',
        success: `Status updated to ${statusConfig[newStatus]?.label || newStatus}`,
        error: 'Failed to update status'
      }
    );
  };

  const printDymoLabel = (listing) => {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, listing.sales_id, {
        format: "CODE128",
        width: 2,
        height: 80,
        displayValue: false,
        margin: 0
      });
      const barcodeDataUrl = canvas.toDataURL("image/png");

      const printWindow = window.open('', '', 'width=400,height=300');
      if (!printWindow) {
          alert('Please allow popups to print labels');
          return;
      }

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Label - ${listing.sales_id}</title>
            <style>
              @page {
                size: 2.3125in 4in; /* DYMO 30256 dimensions */
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0.15in;
                width: 2.3125in;
                height: 4in;
                font-family: Arial, sans-serif;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                box-sizing: border-box;
              }
              .header {
                font-size: 10px;
                font-weight: bold;
                text-transform: uppercase;
                margin-bottom: 5px;
                width: 100%;
                border-bottom: 1px solid black;
                padding-bottom: 2px;
              }
              .product-name {
                font-size: 12px;
                font-weight: bold;
                margin: 5px 0;
                line-height: 1.2;
                max-height: 3.6em;
                overflow: hidden;
                width: 100%;
                word-wrap: break-word;
              }
              .details {
                font-size: 10px;
                margin-bottom: 10px;
                width: 100%;
              }
              .barcode-container {
                margin-top: auto;
                margin-bottom: 10px;
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .barcode-img {
                max-width: 95%;
                height: auto;
              }
              .sales-id-text {
                font-size: 14px;
                font-weight: bold;
                font-family: monospace;
                margin-top: 2px;
              }
              .footer {
                font-size: 9px;
                margin-top: 5px;
              }
            </style>
          </head>
          <body>
            <div class="header">Sales Item Label</div>
            
            <div class="product-name">
              ${listing.productName || 'Unknown Product'}
            </div>
            
            <div class="details">
              <strong>Loc:</strong> ${listing.platform}<br>
              <strong>Grade:</strong> ${listing.grade || 'N/A'}<br>
              ${listing.price ? `<strong>Price:</strong> $${listing.price.toFixed(2)}` : ''}
            </div>

            <div class="barcode-container">
              <img src="${barcodeDataUrl}" class="barcode-img" />
              <div class="sales-id-text">${listing.sales_id}</div>
            </div>
            
            <div class="footer">
              ${new Date().toLocaleDateString()}
            </div>

            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (e) {
      console.error('Print failed', e);
      toast.error('Failed to generate print label');
    }
  };

  // Keyboard shortcut to focus search (Ctrl/Cmd + /)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Sales Listings Report</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage and track all online listings. Scan a Sales ID to quickly find and update items.
        </p>
      </motion.div>

      {/* Filters & Search Toolbar */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Scan Sales ID or search product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
            <div className="absolute right-3 top-2.5 hidden md:block">
              <kbd className="px-2 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-600 dark:text-gray-400 dark:border-gray-500">
                Ctrl + /
              </kbd>
            </div>
          </div>

          <div className="flex gap-4 overflow-x-auto pb-2 md:pb-0">
            <div className="min-w-[150px]">
              <div className="relative">
                <SafeIcon icon={FiFilter} className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="sold">Sold</option>
                  <option value="pending_shipping">Pending Shipping</option>
                  <option value="waiting_for_pickup">Waiting Pickup</option>
                  <option value="canceled">Canceled</option>
                </select>
              </div>
            </div>

            <div className="min-w-[150px]">
              <div className="relative">
                <SafeIcon icon={FiGlobe} className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <select
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="all">All Platforms</option>
                  {platforms.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Listings Table */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sales ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredListings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <SafeIcon icon={FiShoppingBag} className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                    <p className="text-lg font-medium">No listings found</p>
                    <p className="text-sm">Try adjusting your filters or search term</p>
                  </td>
                </tr>
              ) : (
                filteredListings.map((listing) => {
                  const statusInfo = statusConfig[listing.status] || statusConfig['active'];
                  
                  return (
                    <tr key={listing.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                            {listing.productImage ? (
                              <img src={listing.productImage} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <SafeIcon icon={FiShoppingBag} className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]" title={listing.productName}>
                              {listing.productName}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              SKU: {listing.productSku}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 font-mono">
                          <SafeIcon icon={FiTag} className="mr-1.5 h-3 w-3 text-gray-500" />
                          {listing.sales_id || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-900 dark:text-white">{listing.platform}</span>
                          {listing.url && (
                            <a 
                              href={listing.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center mt-0.5"
                            >
                              Link <SafeIcon icon={FiExternalLink} className="ml-1 h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                        ${listing.price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          <SafeIcon icon={statusInfo.icon} className="mr-1.5 h-3 w-3" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {listing.sales_id && (
                            <button
                              onClick={() => printDymoLabel(listing)}
                              className="text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 p-1"
                              title="Print Dymo Label"
                            >
                              <SafeIcon icon={FiPrinter} className="h-4 w-4" />
                            </button>
                          )}
                          
                          <button
                            onClick={() => { setSelectedListing(listing); setShowStatusModal(true); }}
                            className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1"
                            title="Update Status"
                          >
                            <SafeIcon icon={FiRefreshCw} className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => { setSelectedListing(listing); setShowEditModal(true); }}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                            title="Edit Listing"
                          >
                            <SafeIcon icon={FiEdit3} className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400 flex justify-between">
            <span>Showing {filteredListings.length} listings</span>
            <span>Scan a barcode to filter instantly</span>
        </div>
      </motion.div>

      {/* Status Update Modal */}
      <AnimatePresence>
        {showStatusModal && selectedListing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Update Status</h3>
                <button 
                  onClick={() => setShowStatusModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Select a new status for listing <strong className="text-gray-900 dark:text-white">{selectedListing.sales_id}</strong>:
                </p>
                {Object.keys(statusConfig).map(statusKey => (
                  <button
                    key={statusKey}
                    onClick={() => { handleStatusChange(selectedListing, statusKey); setShowStatusModal(false); }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      selectedListing.status === statusKey 
                        ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 ring-1 ring-blue-500' 
                        : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-3 ${
                          statusKey === 'active' ? 'bg-green-500' :
                          statusKey === 'sold' ? 'bg-gray-800' :
                          statusKey === 'canceled' ? 'bg-gray-400' :
                          'bg-yellow-500'
                      }`} />
                      <span className={`text-sm font-medium ${selectedListing.status === statusKey ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>
                        {statusConfig[statusKey].label}
                      </span>
                    </div>
                    {selectedListing.status === statusKey && <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Full Edit Modal */}
      {showEditModal && selectedListing && (
        <AddSalesListingModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          productId={selectedListing.productId}
          existingListing={selectedListing}
        />
      )}
    </div>
  );
};

export default SalesListingReport;